/**
 * Shared logic of the "import photos" panel : a header, a grid of the received photos and an
 * "Importer (N)" button that imports them all. Decoupled from any service so it can back any incoming
 * photos flow (a phone upload, a Google import, ...). No per-photo selection : the grid only shows the
 * photos, the button imports the lot.
 *
 * The header has three states, driven by the photo count and the done event :
 *   - waiting   (no photo yet)      : waitingTitle / waitingSubtitle / waitingText pill (amber)
 *   - receiving (photos arriving)   : title / subtitle / liveText pill (green, pulsing)
 *   - done      (upload finished)   : doneTitle / doneSubtitle / doneText pill (green, check)
 *
 * A footer holds a status line (emptyFooterText / countLabel / doneCountLabel) and the "Quitter" +
 * "Importer (N)" actions. Opened with switchTo("SelectPhotosPanel", { model, photos, options }).
 *
 * This base holds the state logic ; the desktop / mobile subclasses only provide create() (their
 * layout) and their SCSS. They must build : this.title, this.subtitle, this.grid
 * (a shared.components.ReceivingGrid), this.empty, this.footerInfo, this.validateBtn, this.exitBtn ;
 * then call this._update().
 */
fx.ns("shared.panels", class SelectPhotosPanelBase extends fx.Component {

	constructor()
	{
		super();

		this.destroyOnSwitch = true;
	}

	get data()
	{
		return super.data;
	}

	set data(val)
	{
		if (!val)
			return;

		super.data = val.model ? val.model : val;

		this._photos = val.photos || this._photos;

		// `options` override the defaults below, which describe a phone sending its photos. `source`
		// picks another built-in set ; any single key can still be overridden on top of it.
		let o = val.options || {};
		o = Object.assign(this._sourceWording(o.source), o);

		// waiting state (no photo received yet)
		this._waitingTitle = o.waitingTitle || fx.text("Envoyez vos photos depuis votre mobile");
		this._waitingSubtitle = o.waitingSubtitle || fx.text("Ouvrez le lien reçu par SMS et choisissez vos photos.");
		this._emptyText = o.emptyText || fx.text("Nous attendons vos photos…");
		this._emptySubtext = o.emptySubtext || fx.text("Gardez cette page ouverte. Les photos apparaîtront ici dès leur arrivée.");
		this._emptyIllus = o.emptyIllus || this._phoneIllus();
		this._emptyFooterText = o.emptyFooterText || fx.text("Aucune photo reçue");
		this._emptyDoneText = o.emptyDoneText || fx.text("Aucune photo n'a été reçue.");

		// receiving state (photos arriving)
		this._title = o.title || fx.text("Vos photos arrivent");
		this._subtitle = o.subtitle || fx.text("Continuez l'envoi depuis votre mobile, la liste se met à jour toute seule.");
		this._countLabel = o.countLabel || fx.text("$ photos reçues");
		this._countTotalLabel = o.countTotalLabel || fx.text("$ sur $ photos reçues");

		// done state (upload finished)
		this._doneTitle = o.doneTitle || fx.text("Vos photos sont prêtes");
		this._doneSubtitle = o.doneSubtitle || fx.text("Importez-les dans votre album pour commencer la mise en page.");
		this._doneText = o.doneText || "$ photos reçues";
		this._doneCountLabel = o.doneCountLabel || fx.text("$ photos seront ajoutées à votre album");
		this._doneEvent = o.doneEvent || "mobile-upload-finished";

		// how many photos are expected, announced by the source on its own event. Without it there is
		// nothing to measure against and the bar stays hidden — the phone upload never knows its total.
		this._totalEvent = o.totalEvent || "";

		this._buttonLabel = o.buttonLabel || "Importer ($)";
		this._validateEvent = o.validateEvent || "import-photos-validated";
		this._cancelEvent = o.cancelEvent || "import-photos-cancelled";
	}

	// the wording of a flow other than the phone upload the defaults describe
	_sourceWording(source)
	{
		if (source != "google")
			return {};

		return {
			waitingTitle : fx.text("Récupération de vos photos Google"),
			waitingSubtitle : fx.text("Nous les récupérons depuis Google Photos, cela peut prendre un moment."),
			waitingText : fx.text("Connexion à Google Photos"),
			emptyText : fx.text("Nous récupérons vos photos…"),
			emptyDoneText : fx.text("Aucune photo n'a pu être récupérée."),
			emptyIllus : this._cloudIllus(),

			subtitle : fx.text("Elles sont récupérées depuis Google Photos, la liste se met à jour toute seule."),

			// product-neutral : this also backs prints, posters and calendars, which have no album
			doneSubtitle : fx.text("Importez-les pour les ajouter à votre création."),
			doneCountLabel : fx.text("$ photos seront importées"),

			doneEvent : "google-import-finished",
			totalEvent : "google-import-total"
		};
	}

	get count()
	{
		return this._photos ? this._photos.length : 0;
	}

	// the progress bar sitting just above the footer, full width : a track and the fill this panel
	// resizes. Built the same way on every platform, positioned by the subclass.
	_createProgress()
	{
		let bar = new fx.components.Box("import-progress");
		bar.visibleAtStart = false;

		this.progressFill = new fx.components.Box("fill");
		bar.addChild(this.progressFill, { left : 0, top : 0, width : "0%", height : "100%" });

		return bar;
	}

	// the received-photos grid, built the same way on every platform ; the subclass passes its tile
	// size and column rule and does the addChild positioning. Square tiles cropped by the renderer,
	// with the "next photo" placeholder cell appended while receiving.
	_createGrid(maxRowHeight, maxElementsPerRow)
	{
		let grid = new shared.components.ReceivingGrid({ className : "photoGrid",
			data : this._photos,
			maxRowHeight : maxRowHeight,
			maxElementsPerRow : maxElementsPerRow,
			elementWidth : 1,
			elementHeight : 1,
			newChildEffect : "fade",
			renderers : { Photo : shared.renderers.ReceivedPhotoRenderer, NextPhotoPlaceholder : shared.renderers.NextPhotoRenderer }
		});
		return grid;
	}

	onReady()
	{
		super.onReady();

		this.on("click", this._onValidate, this.validateBtn);
		this.on("click", this._onClose, this.exitBtn);

		// photos arriving after the panel is shown : the header, footer count and empty state follow
		if (this._photos)
			this.on("updated", this._update, this._photos);
	}

	// model events bubble here (fx.Component.onDataEvent) : the upload is over, evolve the header
	onDataEvent(e)
	{
		super.onDataEvent(e);

		if (e && this._totalEvent && e.type == this._totalEvent)
		{
			// a total the photos on screen do not already cover announces a new batch. Reopen now rather
			// than on its first photo : until then _done holds the waiting cells at 0 and the bar at
			// full, so a second send changed nothing on screen.
			//
			// Measured against what is on screen, not against the previous total, so it holds for both
			// sources : the phone's counter is cumulative and grows, while a second Google import
			// announces its own batch size after startImport() emptied the grid.
			if (this._done && e.detail > this.count)
				this._reopen();

			this._total = e.detail;
			this._update();   // the total is what the waiting cells are drawn from
		}

		if (e && this._doneEvent && e.type == this._doneEvent)
			this._onDone();
	}

	// header, footer status, button and the empty state, from the current number of photos
	_update()
	{
		let n = this.count;
		let empty = n <= 0;

		// photos arriving again after "done" (the phone app stayed open and sent more) : reopen the
		// receiving state. The done event is re-fired once this new batch settles.
		if (this._done && n > this._doneCount)
			this._reopen();

		if (!this._done)
		{
			this._header(empty);
			this._footerInfo(empty, n);
		}

		this.validateBtn.label = fx.text(this._buttonLabel, n);
		this.validateBtn.disabled = empty;

		// waiting cells : one per photo still to come, so the grid shows the whole import from the
		// first photo on. Until the source announces a total there is nothing to count, so it falls
		// back to the single trailing cell.
		let waiting = this._total ? Math.max(0, this._total - n) : (empty ? 0 : 1);
		this.grid.showPlaceholder(this._done ? 0 : waiting);

		// the waiting card only stands in while there is nothing to draw : once the total is known the
		// grid of waiting cells says the same thing, and says how many
		this.empty.toggle(empty && !this._total);

		this._updateProgress();
	}

	// back to the receiving state : a new batch is on its way, announced either by its total or by its
	// first photo landing
	_reopen()
	{
		this._done = false;
		this.empty.text = this._emptyContent();   // back to the waiting card, spinner and all
	}

	// how far the reception has got, against the total the source announced
	_updateProgress()
	{
		if (!this.progress)
			return;

		let total = this._total || 0;

		this.progress.toggle(total > 0);

		if (total > 0)
			this.progressFill.css("width", Math.min(100, Math.round(this.count * 100 / total)) + "%");
	}

	// title, subtitle and pill for the waiting (no photo) or receiving state. All three are optional :
	// the mobile panel leaves them out and carries the state in its footer line instead.
	_header(waiting)
	{
		if (this.title)
			this.title.text = waiting ? (this._waitingTitle || this._title) : this._title;

		if (this.subtitle)
			this.subtitle.text = waiting ? (this._waitingSubtitle || this._subtitle) : this._subtitle;
	}

	// the footer status line : "Aucune photo reçue" while waiting, else the count — against the total
	// while there is one left to reach, since this line is the only progress the mobile panel shows
	_footerInfo(empty, n)
	{
		let total = this._total || 0;

		// "Aucune photo reçue" only while there is genuinely nothing to say. Once a total is known the
		// line counts against it from the very first poll — including at 0, which is the whole point of
		// announcing it : the customer sees how many are on their way before any has landed.
		if (empty && !total)
		{
			this.footerInfo.text = this._emptyFooterText;
			this.footerInfo.addClass("empty-info");
			return;
		}

		// never against a total smaller than what is on screen : a stale or corrected figure would read
		// "15 sur 10". The plain count is the honest fallback there.
		this.footerInfo.text = (total >= n && total && this._countTotalLabel)
			? fx.text(this._countTotalLabel, '<b>' + n + '</b>', total)
			: (this._countLabel ? fx.text(this._countLabel, '<b>' + n + '</b>') : "");

		this.footerInfo.removeClass("empty-info");
	}

	// the pill : a dot + text ; cls "waiting" = amber static, "receiving" = green pulsing

	// reception finished : evolve the header, footer status and turn the pill into a green check + count
	_onDone()
	{
		this._done = true;
		this._doneCount = this.count;   // a photo past this means a new batch : reopen reception

		this.grid.showPlaceholder(0);   // reception finished : no waiting cells left

		// full, even when photos were dropped on the way : the reception itself is over
		if (this.progress && this._total)
			this.progressFill.css("width", "100%");

		// nothing came in : say so, instead of announcing photos that are not there
		if (this.count <= 0)
		{
			this.empty.text = this._emptyContent(this._emptyDoneText);
			return;
		}

		if (this.title && this._doneTitle)
			this.title.text = this._doneTitle;

		if (this.subtitle && this._doneSubtitle)
			this.subtitle.text = this._doneSubtitle;

		if (this._doneCountLabel)
		{
			this.footerInfo.text = fx.text(this._doneCountLabel, '<b>' + this.count + '</b>');
			this.footerInfo.removeClass("empty-info");
		}

	}

	// the waiting state's markup ; `text` ends it : nothing came, so no spinner and no hint
	_emptyContent(text)
	{
		return '' +
			'<div class="empty-illus">' +
				this._emptyIllus +
			'</div>' +
			'<div class="empty-status">' +
				(text ? '' : '<span class="empty-spinner"></span>') +
				'<span class="empty-text">' + (text || this._emptyText) + '</span>' +
			'</div>' +
			(!text && this._emptySubtext ? '<div class="empty-sub">' + this._emptySubtext + '</div>' : '');
	}

	// photos coming down from a cloud : the Google waiting card
	_cloudIllus()
	{
		return '' +
			'<svg width="150" height="132" viewBox="0 0 180 158" fill="none">' +
				'<path d="M52 66a28 28 0 0 1 54-9 22 22 0 0 1 30 21 21 21 0 0 1-5 14H57a24 24 0 0 1-5-26z" fill="#eef5f9" stroke="#dbe8ef" stroke-width="3" stroke-linejoin="round"/>' +
				'<rect x="34" y="104" width="46" height="46" rx="8" fill="#ffffff" stroke="#dbe8ef" stroke-width="3"/>' +
				'<rect x="42" y="112" width="30" height="30" rx="4" fill="#cdd8c4"/>' +
				'<rect x="100" y="104" width="46" height="46" rx="8" fill="#ffffff" stroke="#dbe8ef" stroke-width="3"/>' +
				'<rect x="108" y="112" width="30" height="30" rx="4" fill="#e7cbb9"/>' +
				'<path d="M90 92v14m0 0-6-6m6 6 6-6" stroke="#9fb9c8" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>' +
			'</svg>';
	}

	// the default illustration : a phone sending its photos
	_phoneIllus()
	{
		return '' +
				'<svg width="126" height="176" viewBox="0 0 150 210" fill="none">' +
					'<rect x="3" y="3" width="144" height="204" rx="26" fill="#ffffff" stroke="#dbe8ef" stroke-width="3"/>' +
					'<rect x="18" y="26" width="114" height="132" rx="12" fill="#eef5f9"/>' +
					'<rect x="30" y="40" width="42" height="42" rx="7" fill="#cdd8c4"/>' +
					'<rect x="78" y="40" width="42" height="42" rx="7" fill="#e7cbb9"/>' +
					'<rect x="30" y="88" width="42" height="42" rx="7" fill="#c4d4e1"/>' +
					'<rect x="78" y="88" width="42" height="42" rx="7" fill="#dcc6d4"/>' +
					'<rect x="46" y="176" width="58" height="12" rx="6" fill="#e6eef3"/>' +
				'</svg>';
	}

	_onValidate()
	{
		this.data.event(this._validateEvent);
	}

	_onClose()
	{
		this.data.event(this._cancelEvent);
	}
});
